import { actionCreator, ActionReturnType, Loading, loadingFailed, loadingFinished, loadingIsLoading, loadingNotStarted } from "@itk/typescript-utils";
import { TAddEditConfigurationDialog, TFormData } from "./add-edit-configuration-dialog-types";
import {
  Model_AttributeGet,
  Model_ConfiguratorProductGet,
  Model_SystemProductConfigurationItemPut,
  Model_SystemProductConfigurationItemExtendedGet,
  Model_ProjectProductsFilterPut,
  Model_SystemPackageGet
} from "src/backend/openapi/v1";
import { SinglePicture } from "src/pages/product-details/product-details-types";

export enum Action {
  SetAddEditConfigurationDialog = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:SETADDEDITCONFIGURATIONDIALOG",

  SetFormData = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:SETFORMDATA",

  GetSystemPackagesRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:GETSYSTEMPACKAGESREQUEST",
  GetSystemPackagesSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:GETSYSTEMPACKAGESSUCCESS",
  GetSystemPackagesFail = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:GETSYSTEMPACKAGESFAIL",

  GetProductsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:GETPRODUCTSREQUEST",
  GetProductsSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:GETPRODUCTSSUCCESS",
  GetProductsFail = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:GETPRODUCTSFAIL",

  GetAttributesRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:GETATTRIBUTESREQUEST",
  GetAttributesSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:GETATTRIBUTESSUCCESS",
  GetAttributesFail = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:GETATTRIBUTESFAIL",

  UpdateProductPictures = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:UPDATEPRODUCTPICTURES",

  CreateOrUpdateConfigurationRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:ADD-EDIT-CONFIGURATION-DIALOG:ADD-EDIT-CONFIGURATION-DIALOG-STORE.TS:ACTION:CREATEORUPDATECONFIGURATIONREQUEST"
}

type TAddEditConfigurationDialogState = {
  addEditConfigurationDialog?: TAddEditConfigurationDialog;
  systemPackagesState: Loading<Model_SystemPackageGet[]>;
  productsState: Loading<Model_ConfiguratorProductGet[]>;
  attributesState: Loading<Model_AttributeGet[]>;
  productPictures: SinglePicture[];
  formData: TFormData;
};

const initialState: TAddEditConfigurationDialogState = {
  addEditConfigurationDialog: undefined,
  systemPackagesState: loadingNotStarted,
  productsState: loadingNotStarted,
  attributesState: loadingNotStarted,
  productPictures: [],
  formData: {
    viewMode: "FULL"
  }
};

export const addEditConfigurationDialogActions = {
  setAddEditConfigurationDialog: (data: TAddEditConfigurationDialogState["addEditConfigurationDialog"]) => actionCreator(Action.SetAddEditConfigurationDialog, { data }),

  setFormData: (data: TAddEditConfigurationDialogState["formData"]) => actionCreator(Action.SetFormData, { data }),

  getSystemPackagesRequest: () => actionCreator(Action.GetSystemPackagesRequest),
  getSystemPackagesSuccess: (data: Model_SystemPackageGet[]) => actionCreator(Action.GetSystemPackagesSuccess, { data }),
  getSystemPackagesFail: () => actionCreator(Action.GetSystemPackagesFail),

  getProductsRequest: (projectId: string, data: Model_ProjectProductsFilterPut) => actionCreator(Action.GetProductsRequest, { projectId, data }),
  getProductsSuccess: (data: Model_ConfiguratorProductGet[]) => actionCreator(Action.GetProductsSuccess, { data }),
  getProductsFail: () => actionCreator(Action.GetProductsFail),

  getAttributesRequest: (attributeEntityFilterId?: string) => actionCreator(Action.GetAttributesRequest, { attributeEntityFilterId }),
  getAttributesSuccess: (data: Model_AttributeGet[]) => actionCreator(Action.GetAttributesSuccess, { data }),
  getAttributesFail: () => actionCreator(Action.GetAttributesFail),

  updateProductPictures: (productPictures: TAddEditConfigurationDialogState["productPictures"]) => actionCreator(Action.UpdateProductPictures, { productPictures }),

  createOrUpdateConfigurationRequest: (data: Model_SystemProductConfigurationItemPut, callBack: (isSuccess: boolean, data?: Model_SystemProductConfigurationItemExtendedGet) => void) =>
    actionCreator(Action.CreateOrUpdateConfigurationRequest, { data, callBack })
};

export const addEditConfigurationDialogReducer = (state = initialState, action: ActionReturnType<typeof addEditConfigurationDialogActions>): TAddEditConfigurationDialogState => {
  switch (action.type) {
    case Action.SetAddEditConfigurationDialog:
      return {
        ...state,
        addEditConfigurationDialog: action.payload.data,
        formData: action.payload.data === undefined ? initialState.formData : state.formData,
        productsState: action.payload.data === undefined ? initialState.productsState : state.productsState,
        // the filter attributes belong to the product type of the item the dialog was opened for, so they must go
        // with it - a top-level flow renders no filter field and would otherwise stay gated by the previous one
        attributesState: action.payload.data === undefined ? initialState.attributesState : state.attributesState
      };

    case Action.SetFormData:
      const newFilter = { ...state.formData.productsFilter, ...action.payload.data.productsFilter };
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload.data,
          productsFilter: newFilter
        }
      };

    case Action.GetSystemPackagesRequest:
      return { ...state, systemPackagesState: loadingIsLoading };
    case Action.GetSystemPackagesSuccess:
      return { ...state, systemPackagesState: loadingFinished(action.payload.data) };
    case Action.GetSystemPackagesFail:
      return { ...state, systemPackagesState: loadingFailed };

    case Action.GetProductsRequest:
      return { ...state, productsState: loadingIsLoading };
    case Action.GetProductsSuccess:
      return { ...state, productsState: loadingFinished(action.payload.data) };
    case Action.GetProductsFail:
      return { ...state, productsState: loadingFailed };

    case Action.GetAttributesRequest:
      return { ...state, attributesState: loadingIsLoading };
    case Action.GetAttributesSuccess:
      return { ...state, attributesState: loadingFinished(action.payload.data) };
    case Action.GetAttributesFail:
      return { ...state, attributesState: loadingFailed };

    case Action.UpdateProductPictures:
      return { ...state, productPictures: [...state.productPictures, ...action.payload.productPictures] };

    default:
      return state;
  }
};
